import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";

import "./IndivididualConfidenceModal.scss";
import { Dialog } from "../../../../../components/dialog/Dialog";
import { NumberSelection } from "../../../../../components/number-selection/NumberSelection";
import { AnalysisConfidenceStorage } from "../../../../../services/organization-analysis-confidence/AnalysisConfidenceStorage";
import { translationService } from "../../../../../services/translation/TranslationService";
import {
  DataSection,
  SubSection,
} from "../../../../../components/data-components/sections/DataSections";
import { AnalysisService } from "../../../../../services/analysis/AnalysisService";
import { CodeDetection, ConfidenceLevels } from "../../../../../api-models/response/VideoAnalysisDataResponse";

export interface IndivididualConfidenceModalProps {
  videoAnalysisService: AnalysisService;
  analysisConfidenceStorage: AnalysisConfidenceStorage;
}

export default observer(function AnalysisIndivididualConfidenceModal({
  videoAnalysisService,
  analysisConfidenceStorage,
}: IndivididualConfidenceModalProps) {
  const [confidenceLevels, setConfidenceLevels] = useState<ConfidenceLevels>({});

  useEffect(() => {
    async function getConfidenceLevels() {
      const confidenceLevels: ConfidenceLevels = {};

      if (analysisConfidenceStorage.AnalysisLevels.length === 0) {
        await analysisConfidenceStorage.load();
      }

      analysisConfidenceStorage.AnalysisLevels.forEach((code) => {
        confidenceLevels[code.name] = code.level * 100;
      });

      setConfidenceLevels(confidenceLevels);
    }

    getConfidenceLevels();
  }, [analysisConfidenceStorage]);

  const confirmIndividualConfidence = () => {
    const processedConfidenceLevels: ConfidenceLevels = {};

    videoAnalysisService.detectedMainCodes.forEach((code) => {
      processedConfidenceLevels[code.code] = confidenceLevels[code.code];
    });

    videoAnalysisService.setIndividualAnalysisConfidence(processedConfidenceLevels);
    videoAnalysisService.hideAnalysisIndivididualConfidenceModal();
  };

  const getNumberSelection = (code: CodeDetection) => (
    <NumberSelection
      key={code.code}
      initialValue={confidenceLevels[code.code]}
      minValue={65}
      maxValue={100}
      step={1}
      labelTranslationKey={translationService.translate("general.add-percentage-at-end", {
        text: code.code,
      })}
      callback={(newValue) => {
        setConfidenceLevels((prev) => {
          return {
            ...prev,
            [code.code]: newValue,
          };
        });
      }}
    />
  );

  return (
    <Dialog
      sidePosition
      isVisible={videoAnalysisService.shouldShowAnalysisIndivididualConfidenceModal}
      onHide={videoAnalysisService.hideAnalysisIndivididualConfidenceModal}
      titleKey={"analysis.analysis-individual-confidence.title"}
      subTextKey={"analysis.analysis-individual-confidence.subtitle"}
      buttonOk={{
        textKey: "general.apply",
        action: confirmIndividualConfidence,
      }}
      buttonCancel={{
        textKey: "general.cancel",
        action: () => {
          videoAnalysisService.hideAnalysisIndivididualConfidenceModal();
        },
      }}
    >
      <DataSection>
        <SubSection>
          {videoAnalysisService.detectedMainCodes
            .slice(undefined, videoAnalysisService.detectedMainCodes.length / 2 + 1)
            .map((code) => getNumberSelection(code))}
        </SubSection>
        <SubSection>
          {videoAnalysisService.detectedMainCodes
            .slice(videoAnalysisService.detectedMainCodes.length / 2 + 1)
            .map((code) => getNumberSelection(code))}
        </SubSection>
      </DataSection>
    </Dialog>
  );
});
